// Version: 1.6
// v1.6: Inputs and selects take their look from index.css (input-sm for compact) (CV-50).
// v1.5: Removed an unused tab style left over from before the shared tabs.
// v1.4: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
// AssetInventory.js — retained solely for BulkImportModal, used in Device Hub
// Full asset management has moved to AssetHub.js and the Assets Hub nav section
import React, { useState, useEffect } from 'react';
import { authFetch } from './auth';

function guessType(host) {
  const ports    = (host.ports || []).map(p => p.port);
  const hostname = (host.hostname || '').toLowerCase();
  const vendor   = (host.vendor   || '').toLowerCase();
  if (ports.includes(515) || ports.includes(9100) || ports.includes(631)) return 'printer';
  if (ports.includes(161)) return 'switch';
  if (vendor.includes('apple')) return 'workstation';
  if (hostname.includes('sw')  || hostname.includes('switch'))            return 'switch';
  if (hostname.includes('ap')  || hostname.includes('wifi'))              return 'access point';
  if (hostname.includes('srv') || hostname.includes('server'))            return 'server';
  if (hostname.includes('nas') || hostname.includes('synology'))          return 'NAS';
  if (hostname.includes('rt')  || hostname.includes('router') || hostname.includes('gw')) return 'router';
  return 'unknown';
}

export function BulkImportModal({ slug, onClose, onImported, defaultTab }) {
  const [activeTab,  setActiveTab]  = useState(defaultTab || 'discovered');
  const [discovered, setDiscovered] = useState([]);
  const [managed,    setManaged]    = useState([]);
  const [existing,   setExisting]   = useState(new Set());
  const [selected,   setSelected]   = useState(new Set());
  const [loading,    setLoading]    = useState(true);
  const [importing,  setImporting]  = useState(false);
  const [progress,   setProgress]   = useState('');
  const [filter,     setFilter]     = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      authFetch(`/api/clients/${slug}/network`).then(r => r.json()).catch(() => ({})),
      authFetch(`/api/clients/${slug}/action1`).then(r => r.json()).catch(() => ({})),
      authFetch(`/api/clients/${slug}/assets`).then(r => r.json()).catch(() => ({ assets: [] })),
    ]).then(([net, a1, assets]) => {
      setDiscovered(net?.hosts || []);
      setManaged(a1?.data?.endpoints || a1?.endpoints || []);
      const ex = new Set();
      (assets.assets || []).forEach(a => {
        if (a.mac)        ex.add(a.mac.toLowerCase());
        if (a.action1_id) ex.add(a.action1_id);
      });
      setExisting(ex);
      setLoading(false);
    });
  }, [slug]);

  const currentList   = activeTab === 'discovered' ? discovered : managed;
  const filteredList  = currentList.filter(d => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return activeTab === 'discovered'
      ? (d.hostname||'').toLowerCase().includes(q) || (d.ip||'').includes(q) || (d.mac||'').toLowerCase().includes(q) || (d.vendor||'').toLowerCase().includes(q)
      : (d.name||'').toLowerCase().includes(q)     || (d.ip||'').includes(q) || (d.user||'').toLowerCase().includes(q) || (d.os||'').toLowerCase().includes(q);
  });

  const isAlreadyIn = (d) => activeTab === 'discovered' ? existing.has((d.mac||'').toLowerCase()) : existing.has(d.id);
  const itemKey     = (d) => activeTab === 'discovered' ? d.mac : d.id;

  const toggleAll = () => {
    const eligible = filteredList.filter(d => !isAlreadyIn(d));
    const keys     = eligible.map(d => itemKey(d));
    const allSel   = keys.every(k => selected.has(k));
    const next     = new Set(selected);
    if (allSel) keys.forEach(k => next.delete(k)); else keys.forEach(k => next.add(k));
    setSelected(next);
  };

  const toggle = (d) => {
    if (isAlreadyIn(d)) return;
    const k    = itemKey(d);
    const next = new Set(selected);
    if (next.has(k)) next.delete(k); else next.add(k);
    setSelected(next);
  };

  const handleImport = async () => {
    const toImport = currentList.filter(d => selected.has(itemKey(d)));
    if (!toImport.length) return;
    setImporting(true);
    let done = 0;
    for (const d of toImport) {
      setProgress(`Adding ${done + 1} of ${toImport.length}…`);
      const body = activeTab === 'discovered'
        ? { name: d.hostname || d.ip, ip: d.ip || '', mac: d.mac || '', hostname: d.hostname || '',
            make: d.vendor || '', source: 'discovered', status: 'active', category: 'device', type: guessType(d) }
        : { name: d.name || '', ip: d.ip || '', hostname: d.name || '', os: d.os || '',
            make: d.manufacturer || '', model: d.model || '', serial: d.serial || '',
            action1_id: d.id || '', owner_name: d.user || '',
            source: 'managed', status: 'active', category: 'device', type: 'workstation' };
      try {
        await authFetch(`/api/clients/${slug}/assets`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } catch {}
      done++;
    }
    setProgress('');
    setImporting(false);
    onImported();
  };

  const eligibleCount = filteredList.filter(d => !isAlreadyIn(d)).length;
  const selectedCount = filteredList.filter(d => selected.has(itemKey(d))).length;
  const allSelected   = eligibleCount > 0 && filteredList.filter(d => !isAlreadyIn(d)).every(d => selected.has(itemKey(d)));


  const inp = {};

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '40px 16px', overflowY: 'auto',
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 14,
        padding: 28, width: '100%', maxWidth: 700,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>Bulk Import to Assets Hub</div>
          <button className="btn btn-quiet btn-icon" onClick={onClose}>×</button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 20 }}>
          Selected devices will be added to this client's Assets Hub. Devices already in the register are shown greyed out.
        </div>

        <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
          <button className="tab-btn" aria-selected={activeTab === 'discovered'} onClick={() => { setActiveTab('discovered'); setFilter(''); }}>
            Discovered Devices {discovered.length > 0 && `(${discovered.length})`}
          </button>
          <button className="tab-btn" aria-selected={activeTab === 'managed'} onClick={() => { setActiveTab('managed'); setFilter(''); }}>
            Managed Computers {managed.length > 0 && `(${managed.length})`}
          </button>
        </div>

        {loading && <div style={{ padding: 32, textAlign: 'center' }}><span className="spinner" /></div>}

        {!loading && (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
              <input className="input-sm" style={{ ...inp, flex: 1 }} placeholder="Filter devices…"
                value={filter} onChange={e => setFilter(e.target.value)} />
              <button className="btn btn-secondary btn-sm" onClick={toggleAll}>
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 16 }}>
              {filteredList.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No devices found.</div>
              )}
              {filteredList.map((d, i) => {
                const already = isAlreadyIn(d);
                const key     = itemKey(d);
                const isSel   = selected.has(key);
                return (
                  <div key={i} onClick={() => toggle(d)} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                    borderBottom: i < filteredList.length - 1 ? '1px solid var(--border)' : 'none',
                    cursor: already ? 'default' : 'pointer',
                    background: isSel ? 'rgba(0,212,255,0.05)' : 'transparent',
                    opacity: already ? 0.4 : 1,
                  }}>
                    <div style={{
                      width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                      border: `2px solid ${isSel ? 'var(--accent)' : 'var(--border)'}`,
                      background: isSel ? 'var(--accent)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {isSel && <span style={{ fontSize: 10, color: '#000', fontWeight: 800 }}>✓</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {activeTab === 'discovered' ? (
                        <>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{d.hostname || d.ip}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                            {d.ip}{d.mac && ` · ${d.mac}`}{d.vendor && ` · ${d.vendor}`}
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{d.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                            {d.os}{d.user && ` · ${d.user}`}{d.ip && ` · ${d.ip}`}
                          </div>
                        </>
                      )}
                    </div>
                    {already && <span style={{ fontSize: 10, color: 'var(--text-muted)', flexShrink: 0 }}>Already in register</span>}
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {selectedCount > 0 ? `${selectedCount} selected` : `${eligibleCount} available to import`}
                {importing && <span style={{ marginLeft: 8 }}>{progress}</span>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button className="btn btn-primary" onClick={handleImport} disabled={importing || selectedCount === 0}>
                  {importing ? (progress || 'Importing…') : `Import ${selectedCount > 0 ? selectedCount + ' ' : ''}Selected`}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
